import { useState } from 'react';
import { Link } from 'react-router';

import { AppHeader } from './AppHeader';
import {
  type MotionPreference,
  useReducedMotionPreference,
  useUiPreferencesStore,
} from '../features/settings/uiPreferencesStore';
import { applySaveToRuntime } from '../features/save/applySaveToRuntime';
import { useSaveStore } from '../features/save/saveStore';
import { readGameplayBaseline } from '../gameplayAnalytics';

const MOTION_OPTIONS: readonly {
  readonly id: MotionPreference;
  readonly label: string;
  readonly detail: string;
}[] = [
  { id: 'system', label: '跟随系统', detail: '使用设备的动态效果偏好' },
  { id: 'full', label: '完整', detail: '显示全部界面与战斗反馈' },
  { id: 'reduced', label: '减少', detail: '关闭非必要动画与粒子反馈' },
];

export function SettingsPage() {
  const audioEnabled = useUiPreferencesStore((state) => state.audioEnabled);
  const hapticsEnabled = useUiPreferencesStore((state) => state.hapticsEnabled);
  const motionPreference = useUiPreferencesStore((state) => state.motionPreference);
  const setAudioEnabled = useUiPreferencesStore((state) => state.setAudioEnabled);
  const setHapticsEnabled = useUiPreferencesStore((state) => state.setHapticsEnabled);
  const setMotionPreference = useUiPreferencesStore((state) => state.setMotionPreference);
  const setTutorialComplete = useUiPreferencesStore((state) => state.setTutorialComplete);
  const reducedMotion = useReducedMotionPreference();
  const [tutorialReset, setTutorialReset] = useState(false);
  const [saveActionMessage, setSaveActionMessage] = useState<string | null>(null);
  const saveStatus = useSaveStore((state) => state.status);
  const saveMessage = useSaveStore((state) => state.message);
  const save = useSaveStore((state) => state.save);
  const exportSave = useSaveStore((state) => state.exportSave);
  const importSave = useSaveStore((state) => state.importSave);
  const resetSave = useSaveStore((state) => state.resetSave);

  return (
    <div className="portal-shell" data-motion={reducedMotion ? 'reduced' : 'full'}>
      <a className="skip-link" href="#settings-content">
        跳到设置内容
      </a>
      <AppHeader />
      <main id="settings-content" className="content-page content-page--narrow">
        <header className="content-page__heading">
          <p className="section-kicker">SYSTEM CONFIGURATION</p>
          <h1>设置</h1>
          <p>偏好保存在当前浏览器，并在下一次进入战斗时自动应用。</p>
        </header>

        <section className="settings-panel" aria-labelledby="feedback-settings">
          <div className="settings-section-heading">
            <span aria-hidden="true">01</span>
            <div>
              <h2 id="feedback-settings">反馈</h2>
              <p>控制战斗音效和支持设备上的触觉反馈。</p>
            </div>
          </div>
          <div className="setting-row">
            <div>
              <strong>战斗音效</strong>
              <small>发射、砖块、技能、Boss 阶段和结算提示</small>
            </div>
            <button
              className="switch-control"
              type="button"
              role="switch"
              aria-label="战斗音效"
              aria-checked={audioEnabled}
              onClick={() => setAudioEnabled(!audioEnabled)}
            >
              <i />
              <span>{audioEnabled ? '开启' : '关闭'}</span>
            </button>
          </div>
          <div className="setting-row">
            <div>
              <strong>震动反馈</strong>
              <small>仅在技能、奖励、Boss 阶段和胜负等关键事件触发</small>
            </div>
            <button
              className="switch-control"
              type="button"
              role="switch"
              aria-label="震动反馈"
              aria-checked={hapticsEnabled}
              onClick={() => setHapticsEnabled(!hapticsEnabled)}
            >
              <i />
              <span>{hapticsEnabled ? '开启' : '关闭'}</span>
            </button>
          </div>
        </section>

        <section className="settings-panel" aria-labelledby="motion-settings">
          <div className="settings-section-heading">
            <span aria-hidden="true">02</span>
            <div>
              <h2 id="motion-settings">动态效果</h2>
              <p>减少动态效果不会改变弹丸速度、Boss 行为或碰撞判定。</p>
            </div>
          </div>
          <div className="motion-options" role="radiogroup" aria-label="动态效果偏好">
            {MOTION_OPTIONS.map((option) => (
              <button
                key={option.id}
                type="button"
                role="radio"
                aria-checked={motionPreference === option.id}
                onClick={() => setMotionPreference(option.id)}
              >
                <i aria-hidden="true" />
                <span>
                  <strong>{option.label}</strong>
                  <small>{option.detail}</small>
                </span>
              </button>
            ))}
          </div>
        </section>

        <section className="settings-panel" aria-labelledby="guide-settings">
          <div className="settings-section-heading">
            <span aria-hidden="true">03</span>
            <div>
              <h2 id="guide-settings">新手引导</h2>
              <p>重置后，下次进入战斗会再次显示三步操作说明。</p>
            </div>
          </div>
          <div className="setting-row">
            <div>
              <strong>首次行动引导</strong>
              <small>{tutorialReset ? '已重置，进入战斗即可查看' : '可随时重新观看'}</small>
            </div>
            <button
              className="settings-secondary-action"
              type="button"
              onClick={() => {
                setTutorialComplete(false);
                setTutorialReset(true);
              }}
            >
              重置引导
            </button>
          </div>
        </section>

        <section className="settings-panel" aria-labelledby="save-settings">
          <div className="settings-section-heading">
            <span aria-hidden="true">04</span>
            <div>
              <h2 id="save-settings">存档与生涯</h2>
              <p>本地存档会自动校验并保留最近三份有效备份。</p>
            </div>
          </div>
          <div className="save-status-card" data-save-status={saveStatus}>
            <div>
              <small>存档状态</small>
              <strong>{formatSaveStatus(saveStatus)}</strong>
              <span>
                {saveActionMessage ?? saveMessage ?? `最近更新 ${formatSaveTime(save.updatedAt)}`}
              </span>
            </div>
            <dl>
              <div>
                <dt>{save.career.totalRuns}</dt>
                <dd>完成行动</dd>
              </div>
              <div>
                <dt>{save.career.bestScore.toLocaleString('zh-CN')}</dt>
                <dd>最高分</dd>
              </div>
              <div>
                <dt>{save.career.totalBricksDestroyed}</dt>
                <dd>累计破砖</dd>
              </div>
              <div>
                <dt>{formatCareerTime(save.career.totalPlaySeconds)}</dt>
                <dd>战斗时间</dd>
              </div>
            </dl>
          </div>
          <div className="save-actions">
            <button
              className="settings-secondary-action"
              type="button"
              onClick={() => {
                downloadSaveFile(exportSave());
                setSaveActionMessage('存档已导出');
              }}
            >
              导出存档
            </button>
            <label className="settings-secondary-action save-import-action">
              导入存档
              <input
                type="file"
                accept="application/json,.json"
                onChange={(event) => {
                  const file = event.currentTarget.files?.[0];
                  event.currentTarget.value = '';
                  if (!file) {
                    return;
                  }
                  void file
                    .text()
                    .then(importSave)
                    .then((imported) => {
                      applySaveToRuntime(imported);
                      setSaveActionMessage('存档已导入并应用');
                    })
                    .catch((error: unknown) => {
                      setSaveActionMessage(
                        `导入失败：${error instanceof Error ? error.message : '文件无效'}`,
                      );
                    });
                }}
              />
            </label>
            <button
              className="settings-danger-action"
              type="button"
              onClick={() => {
                if (!window.confirm('确定清除战役进度、生涯统计和所有偏好吗？')) {
                  return;
                }
                void resetSave().then((reset) => {
                  applySaveToRuntime(reset);
                  setTutorialReset(true);
                  setSaveActionMessage('已建立新的空白存档');
                });
              }}
            >
              重置全部存档
            </button>
          </div>
        </section>

        <section className="settings-panel" aria-labelledby="analytics-settings">
          <div className="settings-section-heading">
            <span aria-hidden="true">05</span>
            <div>
              <h2 id="analytics-settings">玩法数据基线</h2>
              <p>仅汇总本机真实对局行为，不包含身份、存档内容或文本输入。</p>
            </div>
          </div>
          <div className="setting-row">
            <div>
              <strong>本地分析快照</strong>
              <small>用于核对 30 关漏斗、奖励耗尽率、构筑与技能使用率</small>
            </div>
            <button
              className="settings-secondary-action"
              type="button"
              onClick={() => {
                downloadGameplayBaseline();
                setSaveActionMessage('玩法数据基线已导出');
              }}
            >
              导出玩法数据
            </button>
          </div>
          <div className="setting-row">
            <div>
              <strong>内部真人试玩与问题复现</strong>
              <small>记录 20–30 局同构建完整试玩，按 sessionId/runId 打开固定种子</small>
            </div>
            <Link className="settings-secondary-action" to="/playtest">
              打开试玩工作台
            </Link>
          </div>
        </section>

        <div className="settings-footer-actions">
          <Link className="portal-button" to="/">
            返回首页
          </Link>
          <Link className="portal-button portal-button--primary" to="/levels">
            应用并选择关卡
          </Link>
        </div>
      </main>
    </div>
  );
}

function formatSaveStatus(status: ReturnType<typeof useSaveStore.getState>['status']): string {
  if (status === 'ready') return '已校验 · 自动保存';
  if (status === 'recovered') return '已从备份恢复';
  if (status === 'degraded') return '临时存档模式';
  return '正在同步';
}

function formatSaveTime(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '刚刚' : date.toLocaleString('zh-CN');
}

function formatCareerTime(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
}

function downloadSaveFile(serialized: string): void {
  const url = URL.createObjectURL(new Blob([serialized], { type: 'application/json' }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `rogue-brick-breaker-save-${new Date().toISOString().slice(0, 10)}.json`;
  anchor.click();
  URL.revokeObjectURL(url);
}

function downloadGameplayBaseline(): void {
  const serialized = JSON.stringify(readGameplayBaseline(), null, 2);
  const url = URL.createObjectURL(new Blob([serialized], { type: 'application/json' }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `rogue-brick-breaker-baseline-${new Date().toISOString().slice(0, 10)}.json`;
  anchor.click();
  URL.revokeObjectURL(url);
}
